import { type ComponentProps, useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { type OrderLineItem } from '@order/features/orders';

import type { ModalStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToReorderCustomization = (params?: Params) => {
  const { shouldUseModalNavigation, shouldReplaceScreen = false } =
    params ?? {};

  const navigation = useNavigation();
  const modalNavigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToCustomizationScreen = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { lineItemId, addressId, restaurantSlug, customName, product } =
        details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        navigation.navigate('Modal', {
          screen: 'CustomizationDelivery',
          params: {
            reorderLineItemId: lineItemId,
            addressId,
            productSlug,
            ...(customName ? { name: customName } : {}),
          },
        });

        return;
      }

      navigation.navigate('Modal', {
        screen: 'Customization',
        params: {
          reorderLineItemId: lineItemId,
          restaurantSlug,
          productSlug,
          ...(customName ? { name: customName } : {}),
        },
      });
    },
    [navigation],
  );

  const navigateToCustomizationModal = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { navigate, replace } = modalNavigation;

      // NOTE: Reorder can be presented in a small modal on large breakpoints and a
      // fullscreen one on the smallest breakpoint which introduces some issues
      // We use different methods of navigation per breakpoint to avoid unexpected
      // issues.
      const navigationMethod = shouldReplaceScreen ? replace : navigate;

      const { lineItemId, addressId, restaurantSlug, customName, product } =
        details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        navigationMethod('CustomizationDelivery', {
          reorderLineItemId: lineItemId,
          addressId,
          productSlug,
          ...(customName ? { name: customName } : {}),
        });

        return;
      }

      navigationMethod('Customization', {
        reorderLineItemId: lineItemId,
        restaurantSlug,
        productSlug,
        ...(customName ? { name: customName } : {}),
      });
    },
    [modalNavigation, shouldReplaceScreen],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useCallback<NonNullable<OrderLineItemProps['onLineItemCardPress']>>(
    (details) => {
      if (shouldUseModalNavigation) {
        navigateToCustomizationModal(details);

        return;
      }

      navigateToCustomizationScreen(details);
    },
    [
      navigateToCustomizationModal,
      navigateToCustomizationScreen,
      shouldUseModalNavigation,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  shouldUseModalNavigation: boolean;
  shouldReplaceScreen?: boolean;
};

type OrderLineItemProps = ComponentProps<typeof OrderLineItem>;
